import { Box, Typography } from '@mui/material';

import Tabs, { type Tab as DashboardTab } from '@/components/Tabs';
import Tooltip from '@/components/Tooltip';
import { useOverflowObserver } from '@/hooks/useOverflowObserver';

const Title = ({ title }: { title: string | React.ReactNode }) => {
  const [titleRef, isOverflowed] = useOverflowObserver<HTMLHeadingElement>([title]);
  if (typeof title === 'string' || typeof title === 'number') {
    return (
      <Tooltip
        title={isOverflowed ? title : ''}
        variant='secondary'
      >
        <Typography
          ref={titleRef}
          component='h1'
          sx={{
            flex: '0 0 auto',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mr: 4
          }}
          variant='h2'
        >
          {title}
        </Typography>
      </Tooltip>
    );
  }
  return title;
};

export const DashboardLayout = ({
  title,
  children,
  renderRight,
  stickyHeader = true,
  tabNames = [],
  activeTab = 0,
  onTabChange,
  onAddTab,
  onReorderTabs,
  tabsConfig,
  minTabWidth = 140,
  disableTabRipple = false
}: {
  minTabWidth?: number;
  stickyHeader?: boolean;
  title: React.ReactNode;
  children?: React.ReactNode;
  renderRight?: React.ReactNode;
  tabNames?: string[];
  activeTab?: number;
  onTabChange?: (index: number) => void;
  onAddTab?: () => void;
  onReorderTabs?: (sourceIndex: number, targetIndex: number) => void;
  tabsConfig?: DashboardTab[];
  disableTabRipple?: boolean;
}) => {
  const tabsToRender: DashboardTab[] = tabsConfig ?? tabNames.map(name => ({ name }));

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 3,
          minWidth: 0,
          ...(stickyHeader
            ? {
                position: 'sticky',
                top: '-24px',
                backgroundColor: 'background.default',
                zIndex: 10
              }
            : {})
        }}
      >
        <Title title={title} />
        <Box
          sx={{
            flex: 1,
            minWidth: 0
          }}
        >
          {tabsToRender.length > 0 && onTabChange && (
            <Tabs
              activeTab={activeTab}
              disableRipple={disableTabRipple}
              minTabWidth={minTabWidth}
              scrollButtons='auto'
              tabs={tabsToRender}
              onAddTab={onAddTab}
              onChange={onTabChange}
              onReorder={onReorderTabs}
            />
          )}
        </Box>
        <Box sx={{ marginLeft: 'auto', flexShrink: 0 }}>{renderRight}</Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          gap: 3
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
